import React, { useState, useEffect } from 'react';
import { fetchActivePlan, fetchAllPlans, createPlan, updatePlan, deactivatePlan } from '../../services/billingApi';
import type { CompanyPlan } from '../../services/billingApi';
import { useAuth } from '../../contexts/AuthContext';
import './Billing.css';
import { useI18n } from '../../contexts/I18nContext';

export const PlansManager: React.FC = () => {
  const { t } = useI18n();
  const { isAuthenticated, token, isLoading: authLoading } = useAuth();
  const [activePlan, setActivePlan] = useState<CompanyPlan | null>(null);
  const [allPlans, setAllPlans] = useState<CompanyPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<Partial<CompanyPlan>>({
    plan_name: '',
    price: 0,
    billing_cycle: 'monthly',
    features: {},
    limits: {},
  });

  useEffect(() => {
    if (authLoading || !isAuthenticated || !token) {
      return;
    }
    loadPlans();
  }, [authLoading, isAuthenticated, token]);

  const loadPlans = async () => {
    try {
      setLoading(true);
      const [active, all] = await Promise.all([fetchActivePlan(), fetchAllPlans()]);
      setActivePlan(active);
      setAllPlans(all || []);
    } catch (error) {
      console.error('Error loading plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (formData.id) {
        await updatePlan(formData.id, formData);
      } else {
        await createPlan(formData);
      }
      setShowForm(false);
      setFormData({ plan_name: '', price: 0, billing_cycle: 'monthly', features: {}, limits: {} });
      loadPlans();
    } catch (error) {
      console.error('Error saving plan:', error);
      alert(t('plans.errorSave'));
    }
  };

  const handleDeactivate = async (id: string) => {
    if (!confirm('Tem certeza que deseja desativar este plano?')) return;
    try {
      await deactivatePlan(id);
      loadPlans();
    } catch (error) {
      console.error('Error deactivating plan:', error);
      alert(t('plans.errorDeactivate'));
    }
  };

  if (loading) {
    return <div className="billing-loading">{t('plans.loading')}</div>;
  }

  return (
    <div className="billing-container">
      <div className="billing-header">
        <h2>Planos de Cobrança</h2>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary">
          {showForm ? 'Cancelar' : 'Novo Plano'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="billing-form">
          <div className="form-group">
            <label>{t('plans.name')}</label>
            <input
              type="text"
              value={formData.plan_name}
              onChange={(e) => setFormData({ ...formData, plan_name: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>{t('common.price')}</label>
            <input
              type="number"
              step="0.01"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
              required
            />
          </div>
          <div className="form-group">
            <label>{t('plans.billingCycle')}</label>
            <select
              value={formData.billing_cycle}
              onChange={(e) => setFormData({ ...formData, billing_cycle: e.target.value as any })}
              required
            >
              <option value="monthly">Mensal</option>
              <option value="annual">Anual</option>
              <option value="quarterly">Trimestral</option>
              <option value="one_time">Único</option>
            </select>
          </div>
          <button type="submit" className="btn-primary">{t('actions.save')}</button>
        </form>
      )}

      {!activePlan && allPlans.length === 0 && (
        <p className="billing-empty-state">
          Nenhum plano ativo. Configure um plano para habilitar a cobrança SaaS desta empresa.
        </p>
      )}

      {activePlan && (
        <div className="plan-card active">
          <h3>Plano Ativo</h3>
          <p><strong>Nome:</strong> {activePlan.plan_name}</p>
          <p><strong>Preço:</strong> R$ {activePlan.price.toFixed(2)}</p>
          <p><strong>Ciclo:</strong> {activePlan.billing_cycle}</p>
          <p><strong>Ativo desde:</strong> {new Date(activePlan.started_at).toLocaleDateString()}</p>
        </div>
      )}

      <div className="plans-list">
        <h3>{t('plans.history')}</h3>
        {allPlans.length === 0 ? (
          <p className="billing-empty-state">
            Nenhum plano cadastrado. Clique em <strong>{t('plans.new')}</strong> para criar o primeiro plano de cobrança.
          </p>
        ) : (
          allPlans.map((plan) => (
          <div key={plan.id} className={`plan-card ${plan.is_active ? 'active' : ''}`}>
            <p><strong>{plan.plan_name}</strong> - R$ {plan.price.toFixed(2)}/{plan.billing_cycle}</p>
            <p>Status: {plan.is_active ? 'Ativo' : 'Inativo'}</p>
            {plan.is_active && (
              <button onClick={() => handleDeactivate(plan.id)} className="btn-danger">{t('products.deactivate')}</button>
            )}
          </div>
          ))
        )}
      </div>
    </div>
  );
};

